// @ts-nocheck
// Agent Portal (/cti2) — Dialer rail.
// Ported from src/features/cti/screens/IdleScreen.tsx's `panels.dialer`
// block (device switcher, call controls, dial pad, recent calls, collapse
// behavior) — uses the same real cti.css classnames so /cti2 matches the
// production dialer's design exactly; only the layout/composition is new.
//
// Deviation from IdleScreen.tsx (explicit, per product direction): the
// CLI/country-picker block renders LIVE here, whereas IdleScreen.tsx still
// has it behind a dead `{false && ...}` gate. Same data sources
// (assignedClis / cliSource / activeCliId / activeCountryDirectionId), so
// no behavior change once that gate is eventually lifted there too.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiMic,
  FiPause,
  FiRepeat,
  FiUsers,
  FiGrid,
  FiPhone,
  FiPhoneCall,
  FiLoader,
  FiPhoneOff,
  FiVoicemail,
} from 'react-icons/fi';
import { Delete, PanelLeftClose } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { dialKeys } from '@/features/cti/ctiData';
import { getIdleSeconds } from '@/features/cti/ctiHelpers';
import { useCallStore, selectVoicemailReady } from '@/router/zustand/callStore';
import { getUser } from '@/utilities/setting';
import { NUMBERS_TYPES_VMN, NUMBERS_TYPES_TOLFREE } from '@/utilities/constant';
import { loginAsProfile, logoutAsProfile } from '@/features/cti/softphone/loginAsProfile';
import { getAgentMasking, maskNumber } from '@/utilities/cliDisplay';
import { parseAgentCountryDirectionOptions } from '@/utilities/agentCountryCode';
import { postApiCall } from '@/service/httpCall';
import { API_AGENT_MOBILE_DIAL } from '@/service/url';
import { trackActivity } from '@/service/activityTracker';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { toast } from 'sonner';
import { useRecentCalls, formatWorkDuration, sanitizeDialInput, playDtmfTone } from '../utils/dialerFormat';
import DialerCollapsedRail from './DialerCollapsedRail';
import DialerDeviceTabs from './DialerDeviceTabs';
import DialerRecentCalls from './DialerRecentCalls';

const MIN_SPINNER_MS = 400;

const outboundDisabledMessage = 'Outbound calling is disabled for this agent.';
const incomingOnlyCliMessage = 'VMN and TOLL FREE numbers are for incoming calls only. Select a different CLI to make an outbound call.';
const dispositionActiveMessage = 'Finish the active disposition before making a call.';

export default function Dialer({
  userData,
  theme = 'light',
  dialNum,
  setDialNum,
  onDialBack,
  holdOn = false,
  muteOn = false,
  holdLoading = false,
  muteLoading = false,
  onToggleHold,
  onToggleMute,
  onOpenTransfer,
  onOpenConference,
  onAccept,
  onReject,
  onVoicemail,
  voicemailSending = false,
  assignedClis = [],
  loadingAssignedClis = false,
  cliSource = null,
  activeCliId = null,
  onActiveCliChange,
  isOutboundEnabled = false,
  agentCallsData,
  screen = 'idle',
  idleSeconds = 0,
  dispositionSecondsLeft = null,
  dispositionTotalSeconds = null,
  getRemarkForCall,
  contactNameByNumber,
  profileOnly = false,
}) {
  const [dialerCollapsed, setDialerCollapsed] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(false);

  const [activeProfileType, setActiveProfileType] = useState(() => {
    try { return sessionStorage.getItem('activeLoginType'); } catch { return null; }
  });
  const [pendingProfileType, setPendingProfileType] = useState(null);

  const webrtcStatus = useCallStore((s) => s.webrtcPhoneStatus);
  const callState = useCallStore((s) => s.callState);
  const isPlacingCall = useCallStore((s) => s.isPlacingCall);
  const callSetupPending = useCallStore((s) => s.callSetupPending);
  const isAnswering = useCallStore((s) => s.isAnswering);
  const dispositionActive = useCallStore((s) => s.dispositionActive);
  const localHangupRequested = useCallStore((s) => s.localHangupRequested);
  const activeCountryDirectionId = useCallStore((s) => s.activeCountryDirectionId);
  const setActiveCountryDirection = useCallStore((s) => s.setActiveCountryDirection);
  const activeCallMeta = useCallStore((s) => s.activeCallMeta);
  const outgoingRingingActive = useCallStore((s) => s.outgoingRingingActive);
  const sipCampaignId = useCallStore((s) => s.sipCampaignId);
  const profileLogoutPending = useCallStore((s) => s.profileLogoutPending);

  const isOnCall = ['oncall', 'connected', 'hold', 'conference', 'dialing'].includes(callState);
  const isDialing = callState === 'dialing';
  // isOnCall alone is true the moment an outbound call starts DIALING —
  // i.e. while the customer is still just being invited/rung, not yet on
  // the line. Mute/hold/transfer/conference only make sense once there's
  // actually someone to mute/hold/transfer — bridge_time is only ever set
  // at the real answer (both the mobile-socket and SIP paths).
  const hasAnswered = Boolean(activeCallMeta?.bridge_time);

  const [callSeconds, setCallSeconds] = useState(0);
  React.useEffect(() => {
    if (screen !== 'ringing' && screen !== 'oncall') { setCallSeconds(0); return undefined; }
    const startAt = activeCallMeta?.bridge_time || activeCallMeta?.start_time;
    if (!startAt) { setCallSeconds(0); return undefined; }
    setCallSeconds(getIdleSeconds(startAt));
    const interval = setInterval(() => setCallSeconds(getIdleSeconds(startAt)), 1000);
    return () => clearInterval(interval);
  }, [screen, activeCallMeta]);

  React.useEffect(() => {
    const sync = () => {
      try { setActiveProfileType(sessionStorage.getItem('activeLoginType')); } catch {}
    };
    window.addEventListener('sessionStorageChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('sessionStorageChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const configuredProfiles = useMemo(() => {
    const list = getUser()?.agentData?.agentTelephony?.profile || [];
    return new Set(list.map((p) => p?.type).filter(Boolean));
  }, []);
  const currentUserId = useMemo(() => {
    try { return getUser()?.id || null; } catch { return null; }
  }, []);

  const switchInFlightRef = useRef(false);

  const handleToggleProfile = useCallback(async (type) => {
    if (!currentUserId || switchInFlightRef.current || pendingProfileType) return;
    if (!configuredProfiles.has(type)) return;
    switchInFlightRef.current = true;
    setPendingProfileType(type);
    try {
      if (activeProfileType === type) {
        await Promise.all([
          logoutAsProfile(currentUserId, type),
          new Promise((resolve) => setTimeout(resolve, MIN_SPINNER_MS)),
        ]);
        setPendingProfileType(null);
        return;
      }
      if (activeProfileType && activeProfileType !== type) {
        try { await logoutAsProfile(currentUserId, activeProfileType); } catch {}
      }
      await loginAsProfile(currentUserId, type);
      // Not cleared here — loginAsProfile only awaits the login API call
      // and fires the SIP connect click, not the actual SIP registration
      // finishing (that's a separate async handshake demo-1.ts drives).
      // Clearing right away made the spinner vanish well before the
      // device tab was actually connected, so there was no loading
      // feedback for however long registration genuinely takes. The
      // effect below keeps it spinning until parsedStatus resolves one
      // way or the other (or a timeout, so it can never get stuck).
    } catch (err) {
      console.error('[cti2 dialer] profile switch failed', err);
      setPendingProfileType(null);
    } finally {
      switchInFlightRef.current = false;
    }
  }, [currentUserId, activeProfileType, pendingProfileType, configuredProfiles]);

  const isMobileLogin = String(activeProfileType || '').toUpperCase() === 'MOBILE';

  const isVoicemailEnabled = useMemo(() => {
    const fromProp = userData?.agentData?.is_voicemail_enabled ?? userData?.agent_details?.is_voicemail_enabled;
    if (fromProp !== undefined && fromProp !== null) return Boolean(fromProp);
    try {
      const raw = getUser() || {};
      return Boolean(raw?.agentData?.is_voicemail_enabled ?? raw?.agent_details?.is_voicemail_enabled);
    } catch {
      return false;
    }
  }, [userData]);

  const isCampaignCall = Boolean(
    sipCampaignId || activeCallMeta?.campaign_id || activeCallMeta?.campaignId
  );

  const showVoicemail =
    typeof onVoicemail === 'function' &&
    callState === 'incoming' &&
    !outgoingRingingActive &&
    !isCampaignCall &&
    isVoicemailEnabled;
  const voicemailReady = useCallStore(selectVoicemailReady);

  const parsedStatus = useMemo(() => {
    if (isMobileLogin) return { success: true, code: 'READY', message: 'Mobile dialer ready' };
    if (!webrtcStatus) return { success: false, code: 'DISCONNECTED', message: 'Dialer is Disconnected' };
    const status = webrtcStatus.toLowerCase();
    if (status === 'connected') return { success: true, code: 'READY', message: 'Dialer connected' };
    if (status.includes('authenticating')) return { success: false, code: 'AUTH', message: 'Authenticating...' };
    const match = webrtcStatus.match(/^(\d+)\s*-\s*(.*)$/);
    if (match) return { success: false, code: match[1], message: match[2] };
    return { success: false, code: 'ERROR', message: webrtcStatus };
  }, [webrtcStatus, isMobileLogin]);

  // Keeps the device-switch spinner going until parsedStatus (just above)
  // actually resolves connected or failed, rather than clearing the moment
  // the login API call returns (see the comment in handleToggleProfile) —
  // has to sit after parsedStatus is declared, since its dependency array
  // reads it.
  useEffect(() => {
    if (!pendingProfileType || pendingProfileType !== activeProfileType) return undefined;
    if (parsedStatus?.success || (parsedStatus?.code && parsedStatus.code !== 'AUTH' && parsedStatus.code !== 'DISCONNECTED')) {
      setPendingProfileType(null);
      return undefined;
    }
    const timeout = setTimeout(() => setPendingProfileType(null), 15000);
    return () => clearTimeout(timeout);
  }, [pendingProfileType, activeProfileType, parsedStatus]);

  const activeCli = useMemo(
    () => assignedClis.find((c) => Number(c.id) === Number(activeCliId)) || assignedClis[0] || null,
    [assignedClis, activeCliId],
  );
  const hasOutboundCli = Boolean(activeCli?.actual_number);
  const activeCliType = String(activeCli?.number_type || '').trim().toUpperCase();
  const isIncomingOnlyCli = activeCliType === NUMBERS_TYPES_VMN || activeCliType === NUMBERS_TYPES_TOLFREE;
  const profileActive = Boolean(activeProfileType) && Boolean(parsedStatus?.success) && !pendingProfileType && !profileLogoutPending;
  const profileInactiveMessage = 'Activate WebRTC or Mobile profile to call';
  const canUseOutboundDialer = Boolean(isOutboundEnabled && profileActive && hasOutboundCli);
  const canPlaceOutboundCall = Boolean(
    canUseOutboundDialer && !isPlacingCall && !callSetupPending && !isDialing && !isOnCall && !dispositionActive,
  );

  const countryCodeOptions = useMemo(
    () => parseAgentCountryDirectionOptions(userData?.agentData?.agentCountryCodeDirections),
    [userData],
  );
  const activeCountryOption = useMemo(
    () => countryCodeOptions.find((o) => o.id === String(activeCountryDirectionId)) || countryCodeOptions[0] || null,
    [countryCodeOptions, activeCountryDirectionId],
  );
  const showCountryCol = countryCodeOptions.length > 0;
  const showCliCol = assignedClis.length > 0 || loadingAssignedClis;
  const showOutgoingPicker = showCountryCol || showCliCol;
  const cliPickerDisabled = loadingAssignedClis || isOnCall || isPlacingCall || isDialing;

  const handleCountryDirectionChange = useCallback((directionId) => {
    const agentId = userData?.agentData?.id;
    const selected = countryCodeOptions.find((o) => o.id === String(directionId)) || null;
    if (selected) {
      setActiveCountryDirection(selected, agentId);
      trackActivity('country_select', { direction_id: selected.id, calling_code: selected.callingCode || null });
    }
  }, [countryCodeOptions, setActiveCountryDirection, userData]);

  const maskNumbers = getAgentMasking(userData);
  const typedDialRef = useRef('');
  const dialFromSystemRef = useRef(false);
  if (!dialNum) {
    typedDialRef.current = '';
    dialFromSystemRef.current = false;
  } else if (!typedDialRef.current.startsWith(dialNum)) {
    dialFromSystemRef.current = true;
  }
  const showDialMask = Boolean(maskNumbers && dialNum && (dialFromSystemRef.current || screen !== 'idle'));
  const setTypedDialNum = (value) => {
    typedDialRef.current = value;
    setDialNum(value);
  };

  const handleMobileDialerCall = useCallback(async (num) => {
    try {
      const cliNumber = activeCli?.actual_number;
      const agentId = userData?.agentData?.id;
      // An agent with zero country-code directions configured used to be
      // hard-blocked from dialing out at all — the WebRTC softphone
      // (demo-1.ts) already falls back to +91 instead of blocking in this
      // exact situation, so mobile-profile matches that instead of being
      // stricter than the other call path for no real reason.
      const callingCode = String(activeCountryOption?.callingCode || '91').replace(/^\+/, '');
      if (!cliNumber || !agentId) {
        toast.error('Mobile dialer is not fully configured (CLI / agent).');
        return;
      }
      const from = `+${callingCode}${String(cliNumber).replace(/^\+/, '')}`;
      let toDigits = String(num).replace(/^\+/, '');
      if (!toDigits.startsWith(callingCode)) toDigits = `${callingCode}${toDigits}`;
      useCallStore.getState().setPlacingCall(true);
      useCallStore.getState().setCallSetupPending(true);
      const resp: any = await postApiCall(API_AGENT_MOBILE_DIAL, { agent_id: agentId, destination: `+${toDigits}`, from });
      toast.info(resp?.message || `Mobile dialer: calling +${toDigits}`);
      const store = useCallStore.getState();
      store.setOutgoingCall({ number: toDigits });
      store.setPlacingCall(true);
      store.setOutgoingCallStatus('dialing');
      const dialSid = resp?.call_sid || resp?.data?.call_sid || null;
      if (dialSid) store.setSipData({ channelId: dialSid });
    } catch (err: any) {
      const s = useCallStore.getState();
      if (s.callState === 'idle') s.setPlacingCall(false);
      toast.error(friendlyApiError(err) || 'Failed to place call');
    } finally {
      useCallStore.getState().setCallSetupPending(false);
    }
  }, [activeCli, activeCountryOption, userData]);

  const handleDialerCall = useCallback(() => {
    if (!profileActive) { toast.error(profileInactiveMessage); return; }
    if (!isOutboundEnabled) { toast.error(outboundDisabledMessage); return; }
    if (dispositionActive) { toast.error(dispositionActiveMessage); return; }
    if (!activeCli?.actual_number) {
      toast.error('No outbound CLI assigned. Outbound calling needs a number with Outgoing or Both routing.');
      return;
    }
    if (isIncomingOnlyCli) { toast.error(incomingOnlyCliMessage); return; }
    if (!(dialNum && parsedStatus?.success)) return;
    if (String(dialNum).replace(/\D/g, '').length < 5) {
      toast.error('Enter a valid number to call');
      return;
    }
    trackActivity('outgoing_dial', {
      number: dialNum,
      cli_id: activeCli?.id || null,
      cli_number: activeCli?.actual_number || null,
      calling_code: activeCountryOption?.callingCode || null,
      mode: isMobileLogin ? 'mobile' : 'webrtc',
    });
    if (isMobileLogin) {
      handleMobileDialerCall(dialNum);
      return;
    }
    // WebRTC/soft-phone placement binds its own click listener to
    // #callButtonOnIdleScreen (softphone/demo-1) — nothing to call here.
  }, [profileActive, isOutboundEnabled, dispositionActive, activeCli, isIncomingOnlyCli, dialNum, parsedStatus, activeCountryOption, isMobileLogin, handleMobileDialerCall]);

  const recentCalls = useRecentCalls(agentCallsData, contactNameByNumber);

  const dialerControls = [
    // Muting is meaningless while on hold — the far end can't hear the
    // agent either way until they unhold — so it's disabled rather than
    // left clickable for no effect.
    { key: 'mute', icon: <FiMic size={13} />, label: muteOn ? 'Unmute' : 'Mute', active: muteOn, disabled: !hasAnswered || muteLoading || holdOn, onClick: () => onToggleMute?.() },
    { key: 'hold', icon: <FiPause size={13} />, label: holdOn ? 'Unhold' : 'Hold', active: holdOn, disabled: !hasAnswered || holdLoading, onClick: () => onToggleHold?.() },
    { key: 'transfer', icon: <FiRepeat size={13} />, label: 'Transfer', active: false, disabled: !hasAnswered || !isOutboundEnabled, onClick: () => onOpenTransfer?.() },
    { key: 'conf', icon: <FiUsers size={13} />, label: 'Conf', active: false, disabled: !hasAnswered || !isOutboundEnabled, onClick: () => onOpenConference?.() },
    { key: 'keypad', icon: <FiGrid size={13} />, label: 'Keypad', active: keypadOpen, disabled: false, onClick: () => { setKeypadOpen((v) => !v); setDialerCollapsed(false); } },
  ];

  const isDialNumValid = String(dialNum || '').replace(/\D/g, '').length >= 5;
  const hangupLocked = localHangupRequested || callSetupPending;
  const mainAction = isOnCall
    ? (hangupLocked ? undefined : () => useCallStore.getState().requestLocalHangup())
    : (dialNum && isDialNumValid && canPlaceOutboundCall ? handleDialerCall : undefined);

  const workState = screen === 'ringing' || screen === 'oncall'
    ? {
        evt: screen === 'ringing' ? 'ring' : holdOn ? 'hold' : 'live',
        label: holdOn ? 'On hold' : screen === 'ringing' ? 'Ringing' : 'On call',
        seconds: callSeconds,
      }
    : screen === 'disposition'
      ? {
          evt: 'wrap',
          label: 'Wrap-up',
          seconds: dispositionTotalSeconds != null && dispositionSecondsLeft != null
            ? Math.max(0, dispositionTotalSeconds - dispositionSecondsLeft)
            : null,
        }
      : { evt: 'idle', label: 'Idle', seconds: idleSeconds };

  if (profileOnly) {
    return (
      <aside
        className="ap-dialer flex h-full w-[188px] flex-shrink-0 flex-col overflow-hidden rounded-2xl bg-card shadow-neu-raised"
      >
        <div className="cti-app-shell flex h-full min-h-0 flex-1 flex-col" data-theme={theme}>
          <div className="ctiv3-dialer-head shrink-0">
            <span className="ctiv3-dialer-mark"><FiPhoneCall size={13} /></span>
            <span className="ctiv3-dialer-title">Device</span>
          </div>
          <div className="min-h-0 overflow-y-auto p-1">
            <DialerDeviceTabs
              activeProfileType={activeProfileType}
              configuredProfiles={configuredProfiles}
              pendingProfileType={pendingProfileType || (profileLogoutPending ? activeProfileType : null)}
              parsedStatus={parsedStatus}
              onToggleProfile={handleToggleProfile}
            />
            <p className="mt-2 px-1 text-[11px] leading-snug text-muted-foreground">
              {parsedStatus?.success
                ? 'Connected. Incoming calls and monitor/whisper/barge will ring here.'
                : 'Log into a device to receive calls and listen in on your agents.'}
            </p>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      // Read by softphone/demo-1's raw #callButtonOnIdleScreen listener,
      // which places WebRTC calls independently of the React handler above.
      data-incoming-only-cli={isIncomingOnlyCli ? 'true' : undefined}
      className={`ap-dialer flex h-full flex-shrink-0 flex-col overflow-hidden rounded-2xl bg-card shadow-neu-raised ${
        dialerCollapsed ? 'is-collapsed w-16 p-2' : 'w-[292px]'
      }`}
    >
      <AnimatePresence mode="wait" initial={false}>
      {dialerCollapsed ? (
        <DialerCollapsedRail
          onExpand={() => setDialerCollapsed(false)}
          workState={workState}
          screen={screen}
          isMobileLogin={isMobileLogin}
          outgoingRingingActive={outgoingRingingActive}
          onAccept={onAccept}
          isAnswering={isAnswering}
          onVoicemail={showVoicemail ? onVoicemail : null}
          voicemailSending={voicemailSending}
          voicemailReady={voicemailReady}
          dialerControls={dialerControls}
          mainAction={mainAction}
          callBlocked={!isOnCall && !profileActive}
          isOnCall={isOnCall}
          localHangupRequested={localHangupRequested}
        />
      ) : (
        <motion.div
          key="expanded"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="cti-app-shell flex h-full min-h-0 flex-1 flex-col"
          data-theme={theme}
        >
          {/* Header sits outside the padded scroll body below (same
           * panel-header/panel-body split the rest of the CTI uses) so its
           * own 14px inset reads flush with the card edge instead of
           * stacking on top of the body's 12px padding. */}
          <div className="ctiv3-dialer-head shrink-0">
            <span className="ctiv3-dialer-mark"><FiPhoneCall size={13} /></span>
            <span className="ctiv3-dialer-title">Dialer</span>
            <button
              type="button"
              onClick={() => setDialerCollapsed(true)}
              title="Collapse dialer"
              aria-label="Collapse dialer"
              className="ml-auto flex h-7 w-7 items-center justify-center rounded-2xl bg-card text-muted-foreground shadow-neu-raised transition-colors hover:text-foreground"
            >
              <PanelLeftClose size={15} />
            </button>
          </div>

          <div className="min-h-0 overflow-y-auto p-1">
            {/* Connection status used to live here as its own dot+text row,
             * duplicating the dot the active device tab already implies —
             * moved onto the active tab itself below instead (connecting =
             * pulsing amber, connected = green), one dot instead of two. */}
            <div className="ctiv3-device-header">
              <span className="ctiv3-device-header__label">Device</span>
              <span className="ctiv3-device-header__right">
                <span
                  className={`ctiv3-outbound-chip ${isOutboundEnabled ? 'ok' : 'off'}`}
                  title={isOutboundEnabled ? 'Outbound calling enabled for this agent' : outboundDisabledMessage}
                >
                  {isOutboundEnabled ? 'Outbound' : 'No outbound'}
                </span>
              </span>
            </div>

            {/* Device tabs + Idle/status pill share one row now — the tabs
              * used to sit in a fixed 3-column grid regardless of how many
              * actually render (WebRTC/Mobile only, once #55 stopped
              * showing unconfigured ones), leaving the row's own empty
              * space unused while the status pill sat in its own card
              * below. Moved up into that same space instead of duplicating
              * a second row. */}
            <div className="flex items-center gap-2">
              <DialerDeviceTabs
                activeProfileType={activeProfileType}
                configuredProfiles={configuredProfiles}
                pendingProfileType={pendingProfileType || (profileLogoutPending ? activeProfileType : null)}
                parsedStatus={parsedStatus}
                onToggleProfile={handleToggleProfile}
              />
              <div className="ctiv3-state-head ml-auto shrink-0">
                <span className={`call-evt call-evt--${workState.evt}`}>{workState.label}</span>
                {workState.seconds != null && (
                  <span className="ctiv3-state-timer">{formatWorkDuration(workState.seconds)}</span>
                )}
              </div>
            </div>

            {showOutgoingPicker && (
              <div className={`agent-cli-picker${cliPickerDisabled ? ' is-disabled' : ''}`}>
                <div className="agent-cli-picker__head">
                  <span className="agent-cli-picker__icon" aria-hidden>
                    <FiPhone size={14} />
                  </span>
                  <div className="agent-cli-picker__labels">
                    <span className="agent-cli-picker__title">Outgoing</span>
                    <span className="agent-cli-picker__hint">
                      {(() => {
                        const sourceLabelMap = { AGENT: 'Agent', GROUP: 'Group', CALLCENTRE: 'Call Center', NONE: 'None' };
                        const srcLabel = sourceLabelMap[String(cliSource || '').toUpperCase()];
                        return srcLabel ? `Country code & CLI · Source: ${srcLabel}` : 'Country code & CLI';
                      })()}
                    </span>
                  </div>
                </div>

                {loadingAssignedClis && !showCountryCol ? (
                  <div className="agent-cli-picker__loading" aria-busy="true">
                    <span className="agent-cli-picker__skeleton" />
                  </div>
                ) : (
                  <div
                    className={`agent-cli-picker__split${!showCountryCol ? ' agent-cli-picker__split--cli-only' : ''}${!showCliCol ? ' agent-cli-picker__split--country-only' : ''}`}
                  >
                    {showCountryCol ? (
                      <div className="agent-cli-picker__code">
                        {countryCodeOptions.length === 1 ? (
                          <span className="agent-cli-code-display" title="Country code">
                            {activeCountryOption?.display || countryCodeOptions[0].display}
                          </span>
                        ) : (
                          <select
                            value={activeCountryDirectionId ?? activeCountryOption?.id ?? ''}
                            disabled={cliPickerDisabled}
                            onChange={(e) => handleCountryDirectionChange(e.target.value)}
                            className="form-control form-select form-select-sm agent-cli-select agent-cli-select--code"
                            aria-label="Select country code"
                          >
                            {countryCodeOptions.map((opt) => (
                              <option key={opt.id} value={opt.id}>{opt.display}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    ) : null}
                    {showCliCol ? (
                      <div className="agent-cli-picker__cli">
                        {loadingAssignedClis ? (
                          <div className="agent-cli-picker__loading" aria-busy="true">
                            <span className="agent-cli-picker__skeleton" />
                          </div>
                        ) : assignedClis.length === 1 && activeCli?.actual_number ? (
                          <div className="agent-cli-single">
                            <span className="agent-cli-single__number">{activeCli.actual_number}</span>
                          </div>
                        ) : assignedClis.length > 1 ? (
                          <div className="agent-cli-picker__select-wrap">
                            <select
                              value={activeCliId ?? ''}
                              disabled={cliPickerDisabled}
                              onChange={(e) => onActiveCliChange?.(e.target.value)}
                              className="form-control form-select form-select-sm agent-cli-select agent-cli-select--cli"
                              aria-label="Select caller ID"
                            >
                              {assignedClis.map((cli) => (
                                <option key={cli.id} value={cli.id}>{cli.actual_number}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="agent-cli-single agent-cli-single--empty" title="No CLI assigned">
                            <span className="agent-cli-single__number agent-cli-single__number--empty">No CLI assigned</span>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            <div className="ctiv3-ctrl-grid" role="group" aria-label="Call controls">
              {dialerControls.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  title={c.label}
                  disabled={c.disabled}
                  aria-pressed={c.active}
                  onClick={c.onClick}
                  className={`ctiv3-ctl${c.active ? ' active' : ''}`}
                >
                  <span className="ctiv3-ctl-icon">{c.icon}</span>
                  <span className="ctiv3-ctl-label">{c.label}</span>
                </button>
              ))}
            </div>

            <div className="dialer-input-wrap">
              <input
                className="form-control dialer-input"
                style={{ color: showDialMask ? 'transparent' : undefined, caretColor: showDialMask ? 'transparent' : undefined }}
                value={dialNum}
                placeholder="· · · · · · · · · ·"
                id="numberToCall"
                onChange={(e) => {
                  if (!isOutboundEnabled) return;
                  // onKeyDown below already blocks typing a literal '+' (only
                  // digits pass its regex) — country code comes from the
                  // separate picker, not this field. Typing can't get a '+'
                  // in here, but pasting bypasses onKeyDown entirely and
                  // only ever went through sanitizeDialInput, which
                  // deliberately keeps a LEADING '+' (needed when filling
                  // this field from a saved contact/history number that's
                  // already in +<country><number> form via setDialNum
                  // directly, elsewhere) — closing that gap here specifically
                  // for actual typed/pasted user input.
                  const value = sanitizeDialInput(e.target.value).replace(/\+/g, '');
                  setTypedDialNum(value);
                }}
                onCopy={showDialMask ? (e) => e.preventDefault() : undefined}
                onCut={showDialMask ? (e) => e.preventDefault() : undefined}
                onDragStart={showDialMask ? (e) => e.preventDefault() : undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (dialNum && canPlaceOutboundCall) {
                      if (isMobileLogin) handleDialerCall();
                      else document.getElementById('callButtonOnIdleScreen')?.click();
                    }
                    return;
                  }
                  if (e.ctrlKey || e.metaKey || ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) return;
                  if (!isOutboundEnabled || !/^\d$/.test(e.key)) e.preventDefault();
                }}
              />
              {showDialMask ? (
                // Fixed navy-pill-with-white-text mask, independent of
                // theme — same intentional look IdleScreen.tsx uses.
                <div
                  aria-hidden="true"
                  style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', background: 'var(--navy)', borderRadius: 'var(--radius)',
                    padding: '12px 44px 12px 16px', fontSize: '13px', fontWeight: 600, letterSpacing: '2px',
                    color: '#f8fafc', pointerEvents: 'none', userSelect: 'none',
                  }}
                >
                  {maskNumber(dialNum)}
                </div>
              ) : null}
              <button
                type="button"
                className="dial-clear"
                onClick={onDialBack}
              >
                <Delete size={13} />
              </button>
            </div>

            {keypadOpen && (
              <div className="dialpad">
                {dialKeys.map(([num, sub]) => (
                  <button
                    key={num}
                    type="button"
                    className="dial-key keypad"
                    style={{
                      cursor: !parsedStatus?.success || !isOutboundEnabled ? 'not-allowed' : 'pointer',
                      opacity: !parsedStatus?.success || !isOutboundEnabled ? 0.5 : 1,
                    }}
                    disabled={!parsedStatus?.success || !isOutboundEnabled}
                    onClick={() => {
                      if (!isOutboundEnabled) return;
                      playDtmfTone(num);
                      setTypedDialNum(`${dialNum}${num}`);
                    }}
                  >
                    <span className="dial-key-num">{num}</span>
                    <span className="dial-key-sub">{sub || ' '}</span>
                  </button>
                ))}
              </div>
            )}
            {screen === 'ringing' && (
              <div className="grid grid-cols-2 gap-2">
                {/* Answer has no meaning for a mobile-profile incoming call —
                  * it rings the agent's real phone over the carrier network,
                  * there's nothing here to "pick up" in-browser. Reject/Cancel
                  * still applies to both — it used to be hidden for mobile
                  * entirely, leaving no incoming-call affordance at all. */}
                {!outgoingRingingActive && !isMobileLogin && (
                  <button
                    id="call"
                    type="button"
                    onClick={onAccept}
                    disabled={isAnswering}
                    className="flex items-center justify-center gap-1.5 rounded-2xl bg-success py-2 text-sm font-semibold text-success-foreground shadow-neu-raised disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isAnswering
                      ? <><FiLoader size={14} className="animate-spin" /> Answering…</>
                      : <><FiPhoneCall size={14} /> Answer</>}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onReject}
                  disabled={callSetupPending}
                  className={`flex items-center justify-center gap-1.5 rounded-2xl bg-destructive py-2 text-sm font-semibold text-destructive-foreground shadow-neu-raised disabled:cursor-not-allowed disabled:opacity-50 ${outgoingRingingActive || isMobileLogin ? 'col-span-2' : ''}`}
                >
                  <FiPhoneOff size={14} /> {outgoingRingingActive ? 'Cancel' : 'Reject'}
                </button>
                {showVoicemail && (
                  <button
                    type="button"
                    onClick={onVoicemail}
                    disabled={voicemailSending || !voicemailReady}
                    title={voicemailReady ? 'Send this call to voicemail' : 'Waiting for call to connect…'}
                    className="col-span-2 flex items-center justify-center gap-1.5 rounded-2xl bg-muted py-2 text-sm font-semibold text-muted-foreground shadow-neu-raised transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FiVoicemail size={14} /> {voicemailSending ? 'Sending…' : 'Voicemail'}
                  </button>
                )}
              </div>
            )}

            <div
              onClick={mainAction}
              id={isOnCall ? 'hangupButtonOnIdleScreen' : 'callButtonOnIdleScreen'}
              aria-disabled={!mainAction}
              title={isOnCall && localHangupRequested ? 'Ending…' : callSetupPending ? 'Connecting…' : !isOnCall && !profileActive ? profileInactiveMessage : !isOnCall && !isOutboundEnabled ? outboundDisabledMessage : undefined}
              className={`ctiv3-mainbtn${isOnCall ? ' danger' : ' primary'}${!mainAction ? ' disabled' : ''}`}
              style={
                screen === 'ringing'
                  ? { display: 'none' }
                  : (isOnCall && hangupLocked) || callSetupPending
                    ? { opacity: 0.6, cursor: 'not-allowed', pointerEvents: 'none' }
                    : !isOnCall && !profileActive
                      ? { background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))', boxShadow: 'none', opacity: 0.7, cursor: 'not-allowed', pointerEvents: 'none' }
                      : undefined
              }
            >
              {isOnCall ? (
                <>
                  <FiPhoneOff size={14} />
                  <span>{localHangupRequested ? 'Ending…' : callSetupPending ? 'Connecting…' : 'Hang up'}</span>
                </>
              ) : (
                <>
                  <FiPhoneCall size={14} />
                  <span>{callSetupPending ? 'Connecting…' : 'Call'}</span>
                </>
              )}
            </div>
          </div>

          <DialerRecentCalls
            recentCalls={recentCalls}
            maskNumbers={maskNumbers}
            getRemarkForCall={getRemarkForCall}
            onPickNumber={(num) => {
              // The dialer input shows the LIVE caller's number during an
              // active call (locked in AgentPortalPage.tsx) — picking a
              // different number from Recent Calls at that moment silently
              // overwrote it with an unrelated number while still on the
              // real call, which read as if the wrong customer was on the line.
              if (isOnCall) return;
              setDialNum(sanitizeDialInput(num));
            }}
          />
        </motion.div>
      )}
      </AnimatePresence>
    </aside>
  );
}
